import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { canClearPulseTag, canReceivePulseTag, hasUnreadPulseTag, PULSE_TAG_UNDO_MS, sortPulseTagUsers, type PulseTagState, type PulseTagUser } from "@shared/pulse-user-tags";

export interface PulseUserTagsProps {
  pulseId: number;
  projectId: string;
  users: PulseTagUser[];
  states: PulseTagState[];
  viewerId: number;
  viewerRole: string;
  restricted: boolean;
  onChange: (body: {
    userId: number;
    action: "tag" | "urgent" | "clear";
    version?: string;
    message?: string;
    requestId: string;
  }) => Promise<void>;
}

const restrictedReason = "Urgent tags are unavailable because project chat has a wider audience.";
const controlClass = "rounded border border-zinc-600 bg-zinc-800 px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-400 disabled:cursor-not-allowed disabled:opacity-40";
const signature = (state?: PulseTagState) => JSON.stringify(state ?? null);

/** Pure row projection: the parent owns fetching, subscriptions and confirmed state. */
export function PulseUserTags({
  pulseId, projectId, users, states, viewerId, viewerRole, restricted, onChange,
}: PulseUserTagsProps) {
  const stateByUser = new Map(states.map(state => [state.userId, state]));
  const visibleUsers = sortPulseTagUsers(users.filter(user => canReceivePulseTag(user) || stateByUser.has(user.id)), viewerId);
  const [selection, setSelection] = useState<number | "overflow" | null>(null);
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState<Map<number, string>>(() => new Map());
  const [, refreshClock] = useState(0);
  const busy = useRef(false);
  const sent = useRef(new Map<number, string>());
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRecipient = useRef<number | null>(null);
  const drafts = useRef(new Map<number, string>());
  const panel = useRef<HTMLDivElement | null>(null);
  const controls = useRef<HTMLDivElement | null>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const restoringFocus = useRef(false);
  const currentIdentity = useRef("");
  currentIdentity.current = `${projectId}:${pulseId}`;
  const messageId = useId();
  const errorId = useId();
  const internal = viewerRole === "admin" || viewerRole === "team";
  const selectedUser = typeof selection === "number" ? visibleUsers.find(user => user.id === selection) : undefined;
  const selectedState = selectedUser ? stateByUser.get(selectedUser.id) : undefined;
  const waiting = selectedUser ? confirmed.get(selectedUser.id) === signature(selectedState) : false;

  function cancelClose() {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }
  function cancelHover() {
    if (hoverTimer.current !== null) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  }
  function open(userId: number | "overflow", deliberate = false) {
    cancelClose();
    cancelHover();
    if (busy.current || (!deliberate && draftRecipient.current !== null && draftRecipient.current !== userId)) return;
    const draft = typeof userId === "number" ? drafts.current.get(userId) || "" : "";
    if (selection !== userId) setMessage(draft);
    draftRecipient.current = draft && typeof userId === "number" ? userId : null;
    setSelection(userId);
  }
  function hover(userId: number | "overflow") {
    cancelClose();
    cancelHover();
    if (draftRecipient.current !== null || busy.current || selection === userId) return;
    hoverTimer.current = setTimeout(() => { hoverTimer.current = null; open(userId); }, 200);
  }
  function close() {
    cancelHover();
    cancelClose();
    draftRecipient.current = null;
    setSelection(null);
    setMessage("");
  }
  function deferClose() {
    cancelClose();
    cancelHover();
    if (draftRecipient.current !== null || busy.current || panel.current?.contains(document.activeElement)) return;
    // A single transient bridge lets the pointer travel into the portal.
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      close();
    }, 450);
  }
  function focusPanel() {
    cancelClose();
    // Wait for an overflow selection to render its new controls.
    queueMicrotask(() => {
      const target = panel.current?.querySelector<HTMLElement>("textarea:not(:disabled), button:not(:disabled)");
      (target ?? panel.current)?.focus();
    });
  }
  useEffect(() => () => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    if (hoverTimer.current !== null) clearTimeout(hoverTimer.current);
  }, []);
  useEffect(() => {
    cancelHover();
    cancelClose();
    drafts.current.clear();
    draftRecipient.current = null;
    setSelection(null);
    setMessage("");
    setError("");
    setConfirmed(new Map());
    sent.current.clear();
  }, [pulseId, projectId]);

  // Only an open sender-undo panel needs a clock; no per-chip interval.
  useEffect(() => {
    if (!selectedState?.active || selectedState.userId === viewerId || selectedState.taggedBy !== viewerId || !selectedState.taggedAt) return;
    const remaining = Date.parse(selectedState.taggedAt) + PULSE_TAG_UNDO_MS - Date.now();
    if (remaining <= 0 || !Number.isFinite(remaining)) return;
    const timer = setTimeout(() => refreshClock(value => value + 1), remaining + 1);
    return () => clearTimeout(timer);
  }, [selection, selectedState?.version, selectedState?.active, selectedState?.taggedBy, selectedState?.taggedAt, viewerId]);

  async function change(user: PulseTagUser, action: "tag" | "urgent" | "clear", draft = "") {
    const state = stateByUser.get(user.id);
    if (!internal || busy.current || sent.current.get(user.id) === signature(state)) return;
    if (action === "clear") {
      if (!state || !canClearPulseTag(state, viewerId)) return;
    } else if (!canReceivePulseTag(user) || hasUnreadPulseTag(state) || (action === "urgent" && restricted)) return;
    busy.current = true;
    setPending(true);
    setError("");
    const identity = currentIdentity.current;
    const snapshot = signature(state);
    try {
      await onChange({
        userId: user.id, action,
        ...(state ? { version: state.version } : {}),
        ...(action !== "clear" && draft.trim() ? { message: draft.trim() } : {}),
        requestId: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });
      if (identity === currentIdentity.current) {
        sent.current.set(user.id, snapshot);
        setConfirmed(previous => new Map(previous).set(user.id, snapshot));
        drafts.current.delete(user.id);
        draftRecipient.current = null;
        setMessage("");
      }
    } catch (cause) {
      if (identity === currentIdentity.current) {
        setError(cause instanceof Error ? cause.message : "Tag could not be saved. Please try again.");
        open(user.id);
      }
    } finally {
      busy.current = false;
      setPending(false);
    }
  }

  function chip(user: PulseTagUser, overflow = false) {
    const state = stateByUser.get(user.id);
    const status = state?.active ? state.urgent ? "urgent tag" : "active tag" : state?.readAt ? "read, not tagged" : "not tagged";
    const color = user.color || "#a1a1aa";
    const style: CSSProperties = {
      backgroundColor: state?.active ? color : state?.readAt ? `color-mix(in srgb, ${color} 16%, #27272a)` : "#3f3f46",
      color: state?.active ? "#18181b" : state?.readAt ? color : "#a1a1aa",
      borderColor: state?.active || state?.readAt ? color : "#52525b",
    };
    return (
      <button key={user.id} type="button"
        data-pulse-tag-user={user.id} data-tag-status={status}
        aria-label={`${user.name}: ${status}${!canReceivePulseTag(user) ? ", former member" : ""}`}
        aria-haspopup="dialog" aria-expanded={selection === user.id}
        aria-describedby={error ? errorId : undefined}
        className={`relative h-4 min-w-[26px] rounded-sm border px-1 text-[9px] font-semibold leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-300 ${state?.urgent && state.active ? "ring-1 ring-amber-300 ring-offset-1 ring-offset-zinc-900" : ""}`}
        style={style}
        onMouseEnter={() => hover(user.id)} onMouseMove={() => hover(user.id)} onMouseLeave={cancelHover} onFocus={event => {
          opener.current = event.currentTarget;
          if (!restoringFocus.current) open(user.id);
        }}
        onKeyDown={event => {
          event.stopPropagation();
          if (event.key === "ArrowDown" || (event.key === "Tab" && !event.shiftKey && selection === user.id)) {
            event.preventDefault();
            open(user.id);
            focusPanel();
          }
          if (event.key === "Escape") close();
        }}
        onClick={event => {
          event.stopPropagation();
          opener.current = event.currentTarget;
          const switchingDraft = draftRecipient.current !== null && draftRecipient.current !== user.id;
          open(user.id, true);
          if (switchingDraft) { focusPanel(); return; }
          if (!overflow) void change(user, state?.active && (hasUnreadPulseTag(state) || user.id === viewerId) ? "clear" : "tag", selection === user.id ? message : "");
          else focusPanel();
        }}>
        {state?.active && state.urgent && <span aria-hidden="true" className="mr-0.5">!</span>}
        {user.shortCode}
      </button>
    );
  }

  const canClear = internal && !!selectedState && canClearPulseTag(selectedState, viewerId);
  const canTag = internal && !!selectedUser && canReceivePulseTag(selectedUser) && !hasUnreadPulseTag(selectedState);
  const author = selectedState?.taggedBy == null ? null : users.find(user => user.id === selectedState.taggedBy);
  const taggedAt = selectedState?.taggedAt && Number.isFinite(Date.parse(selectedState.taggedAt)) ? new Date(selectedState.taggedAt) : null;

  return (
    <div ref={controls} className="flex min-w-0 items-center gap-1" data-testid={`pulse-user-tags-${pulseId}`}
      onClick={event => event.stopPropagation()} onDoubleClick={event => event.stopPropagation()}>
      <Popover open={selection !== null} onOpenChange={value => { if (!value) close(); }}>
        <PopoverAnchor asChild>
          <div className="flex items-center gap-1" onMouseEnter={cancelClose} onMouseLeave={() => { if (selection !== null) deferClose(); }}>
            <div className="grid grid-cols-2 gap-0.5">
              {visibleUsers.slice(0, 4).map(user => chip(user))}
            </div>
            {visibleUsers.length > 4 && <button type="button" className="h-5 rounded px-1 text-[10px] text-zinc-400 hover:bg-zinc-800 focus-visible:outline focus-visible:outline-zinc-400"
              aria-label={`Show ${visibleUsers.length - 4} more members`} aria-haspopup="dialog" aria-expanded={selection === "overflow"}
              onFocus={event => { opener.current = event.currentTarget; if (!restoringFocus.current) open("overflow"); }} onMouseEnter={() => hover("overflow")} onMouseMove={() => hover("overflow")} onMouseLeave={cancelHover}
              onKeyDown={event => {
                event.stopPropagation();
                if (event.key === "ArrowDown" || (event.key === "Tab" && !event.shiftKey)) {
                  event.preventDefault(); open("overflow"); focusPanel();
                }
              }}
              onClick={event => { opener.current = event.currentTarget; open("overflow", true); focusPanel(); }}>+{visibleUsers.length - 4}</button>}
            {visibleUsers.length === 0 && <span className="text-[10px] text-zinc-500" aria-label="No assigned members">—</span>}
          </div>
        </PopoverAnchor>
        {selection !== null && <PopoverContent ref={panel} tabIndex={-1} side="right" align="start" sideOffset={6}
          className="w-64 max-w-[calc(100vw-24px)] border-zinc-700 bg-zinc-900 p-3 text-zinc-200 shadow-xl"
          aria-label={selectedUser ? `Tag ${selectedUser.name}` : "More pulse members"}
          onInteractOutside={event => {
            // Chip clicks deliberately switch recipients; don't let the portal's
            // outside-dismiss handler clear the draft lock before that click.
            if (event.target instanceof Node && controls.current?.contains(event.target)) event.preventDefault();
          }}
          onOpenAutoFocus={event => event.preventDefault()} onCloseAutoFocus={event => {
            event.preventDefault();
            // Restore only when focus was in the panel, not after an outside click.
            if (document.activeElement !== document.body) return;
            if (opener.current?.isConnected) {
              restoringFocus.current = true;
              opener.current.focus();
              queueMicrotask(() => { restoringFocus.current = false; });
            }
          }}
          onMouseEnter={() => { cancelClose(); cancelHover(); }} onMouseLeave={deferClose} onFocusCapture={() => { cancelClose(); cancelHover(); }}
          onKeyDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
          {selection === "overflow" ? (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">More members</p>
              {visibleUsers.slice(4).map(user => <div key={user.id} className="flex items-center gap-2">
                {chip(user, true)}
                 <span className="text-xs">{user.name}{!canReceivePulseTag(user) && <span className="ml-1 text-zinc-500">(former)</span>}</span>
              </div>)}
            </div>
          ) : selectedUser ? (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold">{selectedUser.name}</span>
                 {!canReceivePulseTag(selectedUser) && <span className="text-[10px] text-zinc-400">Former member</span>}
              </div>
              {selectedState?.message && <blockquote className="break-words whitespace-pre-wrap border-l-2 border-zinc-500 bg-zinc-800/70 px-2 py-1.5 text-xs">{selectedState.message}</blockquote>}
              {selectedState?.taggedBy != null && <p className="text-[10px] text-zinc-400">
                {selectedState.active ? "Tagged" : "Last tagged"} by {author?.name ?? `User ${selectedState.taggedBy}`}
                {taggedAt && <> · <time dateTime={taggedAt.toISOString()}>{taggedAt.toLocaleString()}</time></>}
              </p>}
              {canTag && <>
                <label className="block text-[10px] text-zinc-400" htmlFor={messageId}>Message to {selectedUser.name} <span className="text-zinc-500">(optional)</span></label>
                <textarea id={messageId} value={message} onChange={event => {
                  cancelHover();
                  cancelClose();
                  const value = event.target.value;
                  drafts.current.set(selectedUser.id, value);
                  draftRecipient.current = value ? selectedUser.id : null;
                  setMessage(value);
                }}
                  rows={2} disabled={pending || waiting}
                  className="w-full resize-none rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-xs placeholder:text-zinc-500 focus:border-zinc-400 focus:outline-none"
                  placeholder="What needs their attention?" />
              </>}
              {selectedState?.active && <>
                <p className="text-[11px] text-zinc-400">{hasUnreadPulseTag(selectedState)
                  ? `${selectedState.urgent ? "Urgent tag" : "Active tag"} · waiting to be read.`
                  : "This tag has been seen. You can tag this person again."}</p>
                {canClear && <button type="button" className={controlClass} disabled={pending || waiting}
                  onClick={() => void change(selectedUser, "clear")}>{selectedUser.id === viewerId ? "Clear my tag" : "Undo tag"}</button>}
              </>}
              {canTag ? <>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={`${controlClass} border-amber-700/60 text-amber-200`} disabled={restricted || pending || waiting}
                    title={restricted ? restrictedReason : undefined}
                    onClick={() => void change(selectedUser, "urgent", message)}>Urgent tag {selectedUser.name}</button>
                  <button type="button" className={controlClass} disabled={pending || waiting}
                    onClick={() => void change(selectedUser, "tag", message)}>Tag {selectedUser.name}</button>
                </div>
                {restricted && <p className="text-[10px] leading-relaxed text-zinc-400">{restrictedReason}</p>}
               </> : !canReceivePulseTag(selectedUser) && <p className="text-[11px] text-zinc-400">Former members cannot receive new tags.</p>}
              {!internal && <p className="text-[11px] text-zinc-400">Only internal team members and admins can change tags.</p>}
              {(pending || waiting) && <p role="status" className="text-[11px] text-zinc-400">{pending ? "Saving tag…" : "Saved; waiting for the table to update."}</p>}
              {error && <p role="alert" className="break-words text-xs text-red-300">{error}</p>}
            </div>
          ) : <p className="text-xs text-zinc-400">This member is no longer available.</p>}
        </PopoverContent>}
      </Popover>
      {error && <span id={errorId} role="alert" className="max-w-32 text-[10px] text-red-300">{error}</span>}
    </div>
  );
}
